import {useRef, useState} from 'react';
import {Link, NavLink, useNavigate} from 'react-router-dom';
import {
  LayoutDashboard,
  Phone,
  CheckSquare,
  Inbox,
  Bell,
  Settings,
  FlaskConical,
  HelpCircle,
  Users,
  UsersRound,
  Building2,
  PlugZap,
  Activity,
  ListChecks,
  BookUser,
  BarChart3,
} from 'lucide-react';
import {useAtom} from 'jotai';
import {notificationsOpenAtom} from '@lib/atoms';
import {useNotificationsCount} from '@api/notifications';
import {cn} from '@lib/utils';
import {Button} from '@ui/shadcn/button';
import {GmkLogo} from '@ui/brand/GmkLogo';
import {ColverLogo} from '@ui/brand/ColverLogo';
import {BRAND, IS_GMK} from '@lib/brand';
import {ProfileMenu} from './ProfileMenu';
import {useAuth} from '@shared/api/auth';
import {
  hasAction,
  useActiveWorkspace,
  useMyAccess,
  usePersonalScope,
  useWorkspaceModules,
} from '@api/workspaces';

const getUserInitials = (
  displayName?: string | null,
  email?: string,
): string => {
  if (displayName) {
    const parts = displayName.trim().split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return displayName.slice(0, 2).toUpperCase();
  }
  if (email) return email.slice(0, 2).toUpperCase();
  return '?';
};

interface NavItem {
  to: string;
  icon: typeof LayoutDashboard;
  label: string;
}

/** Рабочее пространство — видно каждому участнику воркспейса. */
const WORKSPACE_NAV: NavItem[] = [
  {to: '/dashboard', icon: LayoutDashboard, label: 'Дашборд'},
  {to: '/calls', icon: Phone, label: 'Звонки'},
  {to: '/meetings', icon: UsersRound, label: 'Встречи'},
  {to: '/clients', icon: BookUser, label: 'Клиенты'},
  {to: '/tasks', icon: CheckSquare, label: 'Задачи'},
  {to: '/processed', icon: Inbox, label: 'Обработанные'},
];

/** Администрирование сервиса — только операторы платформы (`isOperator`). */
const OPERATOR_NAV: NavItem[] = [
  {to: '/admin/clients', icon: Building2, label: 'Клиенты'},
  {to: '/admin/sources', icon: PlugZap, label: 'Источники'},
  {to: '/admin/monitoring', icon: Activity, label: 'Мониторинг'},
];

/**
 * Пункт сайдбара. Цвета — токены `--sidebar-*` бренда: у GMK светлая иконка
 * на GMK GRAY и активный GMK RED, у Colver — как в продукте.
 */
const sidebarItemCls = (isActive: boolean) =>
  cn(
    'w-full flex items-center justify-center h-9 rounded-lg transition-colors',
    isActive
      ? 'bg-[var(--sidebar-active-bg)] text-[var(--sidebar-active-fg)] hover:bg-[var(--sidebar-active-bg)] hover:text-[var(--sidebar-active-fg)]'
      : 'text-[var(--sidebar-fg)] hover:bg-[var(--sidebar-hover-bg)] hover:text-[var(--sidebar-fg-strong)]',
  );

const navLinkCls = ({isActive}: {isActive: boolean}) => sidebarItemCls(isActive);

/** Горизонтальный разделитель секций навигации. */
const NavSeparator = () => (
  <div className='w-7 my-1 border-t border-[var(--sidebar-separator)]' aria-hidden />
);

/**
 * Боковая навигационная панель (60px, icon-only), секциями:
 * рабочее пространство → управление воркспейсом (по грантам) → администрирование
 * сервиса (только оператор).
 */
export const Sidebar = () => {
  const [notifOpen, setOpen] = useAtom(notificationsOpenAtom);
  const {data: unread = 0, isError: notificationCountError} =
    useNotificationsCount();
  const navigate = useNavigate();
  const {currentUser} = useAuth();
  const {activeId} = useActiveWorkspace();
  const {data: access} = useMyAccess(activeId);

  // Управление воркспейсом: «Команда» — всем участникам; песочница и настройки —
  // по соответствующим действиям (роль «Администратор воркспейса» даёт оба).
  // Песочница (планы + коммуникации) видна по гранту manage-plans, а также
  // оператору платформы — даже если он не админ конкретного воркспейса.
  // Метрики сотрудников — по view-analytics: админ, комдир, РОП (не менеджер).
  const canSeeMetrics = hasAction(access, 'view-analytics');
  const metricsItem: NavItem = {
    to: '/user-metrics',
    icon: BarChart3,
    label: 'Метрики',
  };
  const manageNav: NavItem[] = [
    {to: '/team', icon: Users, label: 'Команда'},
    ...(canSeeMetrics ? [metricsItem] : []),
    ...(hasAction(access, 'manage-workspace') || currentUser?.isOperator
      ? [
          {
            to: '/communications',
            icon: ListChecks,
            label: 'Все коммуникации',
          } as NavItem,
        ]
      : []),
    ...(hasAction(access, 'manage-plans') || currentUser?.isOperator
      ? [{to: '/sandbox', icon: FlaskConical, label: 'Песочница'} as NavItem]
      : []),
    ...(hasAction(access, 'manage-workspace')
      ? [
          {
            to: '/settings/workspace',
            icon: Settings,
            label: 'Настройки воркспейса',
          } as NavItem,
        ]
      : []),
  ];

  // Рабочие разделы, включая задачи, доступны всем ролям.
  // Административные разделы — только workspace-admin / оператору.
  const isAdmin =
    hasAction(access, 'manage-workspace') || Boolean(currentUser?.isOperator);
  const {isPersonal, isReady} = usePersonalScope();
  const modules = useWorkspaceModules();
  const workspaceNav = WORKSPACE_NAV.filter((item) => {
    // Модуль «Встречи» выключен: у РОП, коммерческого директора и админа пункт
    // остаётся — он ведёт на заглушку с предложением подключить модуль. У
    // менеджера раздела нет. Пока роль не определена, прячем (fail-closed):
    // иначе пункт мигнул бы у менеджера.
    if (
      item.to === '/meetings' &&
      !modules.meetings &&
      (!isReady || isPersonal)
    )
      return false;
    return true;
  });
  // Комдиру и РОПу «Управление» не показываем — «Метрики» у них в основном меню.
  const sections: NavItem[][] = isAdmin
    ? [workspaceNav, manageNav]
    : [canSeeMetrics ? [...workspaceNav, metricsItem] : workspaceNav];
  if (currentUser?.isOperator) sections.push(OPERATOR_NAV);

  const avatarRef = useRef<HTMLButtonElement>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [anchor, setAnchor] = useState<{left: number; bottom: number} | null>(
    null,
  );

  const toggleMenu = () => {
    if (menuOpen) {
      setMenuOpen(false);
      return;
    }
    if (!avatarRef.current) return;
    const rect = avatarRef.current.getBoundingClientRect();
    // Якорим popover нижним левым углом к правому верхнему краю аватарки —
    // меню «вылетает» вверх и вправо от иконки сайдбара.
    setAnchor({
      left: rect.right + 6,
      bottom: window.innerHeight - rect.top,
    });
    setMenuOpen(true);
  };

  return (
    <aside
      className='fixed left-0 top-0 bottom-0 flex flex-col items-center pb-3 gap-1 bg-[var(--sidebar-bg)] border-r border-[var(--sidebar-border)]'
      style={{
        paddingTop: '0.75rem',
        width: 'var(--sidebar-w)',
        zIndex: 'var(--z-sidebar)',
      }}
    >
      {/* Знак бренда: GMK — красный на GMK GRAY (вариант из брендбука), Colver — логотип продукта. */}
      <Link
        to='/dashboard'
        title={BRAND.productName}
        className={cn('flex items-center justify-center', IS_GMK ? 'mb-4 mt-1' : 'mb-2')}
      >
        {IS_GMK ? <GmkLogo height={13} /> : <ColverLogo size={32} />}
      </Link>

      {/* Навигация секциями с разделителями. */}
      <nav className='flex flex-col items-center gap-1 w-full px-2'>
        {sections.map((items, sectionIdx) =>
          items.length === 0 ? null : (
            <div
              key={sectionIdx}
              className='w-full flex flex-col items-center gap-1'
            >
              {sectionIdx > 0 && <NavSeparator />}
              {items.map(({to, icon: Icon, label}) => (
                <NavLink
                  key={to}
                  to={to}
                  title={label}
                  onClick={() => setOpen(false)}
                  className={navLinkCls}
                >
                  <Icon size={18} />
                </NavLink>
              ))}
            </div>
          ),
        )}

        {/* Уведомления (колокольчик). */}
        <Button
          type='button'
          variant='ghost'
          onClick={() => setOpen(true)}
          title='Уведомления'
          className={cn('relative', sidebarItemCls(notifOpen))}
        >
          <Bell size={18} />
          {!notificationCountError && unread > 0 && (
            // min-w вместо фиксированной ширины: «7» остаётся кружком, а «99+»
            // растягивает бейдж в пилюлю, а не обрезается.
            <span className='absolute top-0.5 right-1.5 min-w-4 h-4 px-1 rounded-full bg-[var(--sidebar-badge-bg)] ring-2 ring-[var(--sidebar-bg)] text-white text-[10px] font-bold flex items-center justify-center tabular-nums'>
              {unread > 99 ? '99+' : unread}
            </span>
          )}
        </Button>
      </nav>

      {/* Группа внизу: поддержка + аватарка. mt-auto прижимает к нижнему краю. */}
      <div className='mt-auto flex flex-col items-center gap-1 mb-1'>
        <Button
          type='button'
          variant='ghost'
          size='icon'
          onClick={() => navigate('/support')}
          title='Поддержка'
          className='text-[var(--sidebar-fg)] hover:bg-[var(--sidebar-hover-bg)] hover:text-[var(--sidebar-fg-strong)]'
        >
          <HelpCircle size={18} />
        </Button>

        <Button
          ref={avatarRef}
          type='button'
          variant='ghost'
          size='icon'
          onClick={toggleMenu}
          title='Профиль'
          className={cn(
            'rounded-full text-xs font-semibold',
            'bg-[var(--sidebar-avatar-bg)] text-[var(--sidebar-avatar-fg)] hover:bg-[var(--sidebar-avatar-bg)] hover:text-[var(--sidebar-avatar-fg)]',
            menuOpen && 'ring-2 ring-[var(--sidebar-ring)]',
          )}
        >
          {getUserInitials(currentUser?.displayName, currentUser?.email)}
        </Button>
      </div>

      {menuOpen && anchor && (
        <ProfileMenu anchor={anchor} onClose={() => setMenuOpen(false)} />
      )}
    </aside>
  );
};
